import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { BidDayGroup } from '@bidmanager/shared';
import { errorMessage, request } from '../api';

/**
 * The daily bid list (§2.2.3).
 *
 * Days come from the API already grouped in the client's configured timezone.
 * Doing it here would mean grouping by the *viewer's* browser timezone, so a
 * client checking from a trip would see their team's output shuffled across
 * days — a report that changes depending on where you read it.
 */
export function Bids(): JSX.Element {
  const { data, error, isLoading } = useQuery({
    queryKey: ['bids'],
    queryFn: () => request<BidDayGroup[]>('GET', '/bids'),
  });

  if (isLoading) return <p className="muted">Loading bids…</p>;
  if (error) return <p className="error">{errorMessage(error)}</p>;

  const total = data?.reduce((sum, group) => sum + group.count, 0) ?? 0;

  return (
    <>
      <header className="page-head">
        <h1>Bids</h1>
        <p className="muted small" style={{ margin: 0 }}>
          {total} recorded {total === 1 ? 'bid' : 'bids'}. Open one to see the job description and
          the exact documents that were submitted.
        </p>
      </header>

      {data?.length === 0 && (
        <div className="card muted">
          No bids yet. Once a bidder submits an application it appears here with its screenshots.
        </div>
      )}

      <div className="stack">
        {data?.map((group) => (
          <section key={group.day} className="card">
            <div className="row" style={{ marginBottom: 10 }}>
              <strong>{group.day}</strong>
              <span className="muted small">
                {group.count} {group.count === 1 ? 'bid' : 'bids'} · times in {group.timezone}
              </span>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Role</th>
                  <th>Profile</th>
                  <th>Bidder</th>
                  <th>Time</th>
                  <th>Shots</th>
                  <th>Match</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {group.bids.map((bid) => (
                  <tr key={bid.id}>
                    <td>{bid.companyName}</td>
                    <td className="truncate">{bid.role}</td>
                    <td className="muted">{bid.profileName}</td>
                    <td className="muted">{bid.bidderName}</td>
                    <td className="muted">
                      {/*
                        Formatted in the CLIENT's timezone, the same one the
                        day header was grouped by. Using the browser's default
                        would put the header and the clock time in different
                        zones — a bid filed under one day showing a time that
                        belongs to another.
                      */}
                      {new Date(bid.placedAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: group.timezone,
                      })}
                    </td>
                    <td className={bid.screenshotCount === 0 ? 'warn' : 'muted'}>
                      {bid.screenshotCount}
                    </td>
                    <td>
                      {bid.jdMatchPercent !== null && (
                        <span
                          className={`badge ${bid.jdMatchPercent >= 60 ? 'ok' : 'warn'}`}
                          title="How much of the posting these documents address — a coverage check, not an ATS score."
                        >
                          {bid.jdMatchPercent}%
                        </span>
                      )}
                    </td>
                    <td>
                      <Link to={`/bids/${bid.id}`}>Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </>
  );
}
